import { ComponentClass, createElement, CSSProperties, FunctionComponent } from "react";
import { Virtualizer } from "@tanstack/react-virtual";
import { Column } from "@tanstack/table-core";

export const getCommonPinningStyles = (column: Column<Object>): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    borderRight: isLastLeftPinnedColumn ? "1px solid var(--border-secondary)" : undefined,
    borderLeft: isFirstRightPinnedColumn ? "1px solid var(--border-secondary)" : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    position: isPinned ? "sticky" : "relative",
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

export function renderCells<
  TScrollElement extends Element | Window,
  TItemElement extends Element,
>
(
  virtualizer: Virtualizer<TScrollElement, TItemElement>,
  virtualize: boolean = false,
  columns: Column<object, unknown>[],
  renderCell: (index: number) => React.ReactNode,
  element: FunctionComponent<any> | ComponentClass<any> | string = "th",
) {
  if (!virtualize) {
    return (
      <>
        {
          columns.map((_, index) => renderCell(index))
        }
      </>
    );
  }

  const virtualItems = virtualizer.getVirtualItems();
  const allColumns = columns.map((column, index) => ({ column, index }));
  const leftPinnedColumns = allColumns.filter(({ column }) => column.getIsPinned() === "left");
  const rightPinnedColumns = allColumns.filter(({ column }) => column.getIsPinned() === "right");

  let virtualPaddingLeft: number = 0;
  let virtualPaddingRight: number = 0;

  if (virtualizer && virtualItems?.length) {
    virtualPaddingLeft = virtualItems[0]?.start ?? 0;
    virtualPaddingRight =
      virtualizer.getTotalSize() -
      (virtualItems[virtualItems.length - 1]?.end ?? 0);

    const getSize = (index: number) => {
      const offsetForIndex = virtualizer.getOffsetForIndex(index);
      const item = virtualizer.getVirtualItemForOffset((offsetForIndex as any)[0]);
      return item!.size;
    };

    if (virtualPaddingLeft) {
      virtualPaddingLeft = virtualPaddingLeft - leftPinnedColumns.reduce((acc, { index }) => acc + getSize(index), 0);
    }
    if (virtualPaddingRight) {
      virtualPaddingRight = virtualPaddingRight - rightPinnedColumns.reduce((acc, { index }) => acc + getSize(index), 0);
    }
  }

  return (
    <>
      {leftPinnedColumns.map(({ index }) => renderCell(index))}
      {virtualPaddingLeft > 0 ? (
        createElement(element, { style: { display: "flex", width: virtualPaddingLeft } })
      ) : null}
      {virtualItems.map(vc => !allColumns[vc.index].column.getIsPinned() ? renderCell(vc.index) : null)}
      {virtualPaddingRight > 0 ? (
        createElement(element, { style: { display: "flex", width: virtualPaddingRight } })
      ) : null}
      {rightPinnedColumns.map(({ index }) => renderCell(index))}
    </>
  );
}
